import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  GenericDataSource_CapableOfBeingIntersected,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
  SetBillingAddressOnCartInput,
} from "../../../Common";

import {
  CheckoutModel_MyBag,
  CheckoutModel_MyBag_Graphql_Request,
} from "../../model";

import { useGenericDataPortForMyBag } from "../use-generic-data-port-for-mybag";

import { DataMutation_Shipping_SetBillingAddressesOnCart_QueryKey } from "./data-mutation--shipping-set-billing-addresses-on-cart--query-key-factory";

export interface DataMutationShippingSetBillingAddressesOnCartConsumerInterface
  extends GenericDataSource_CapableOfBeingIntersected<
    DataMutationShippingSetBillingAddressesOnCart_Result["data"]
  > {
  data: DataMutationShippingSetBillingAddressesOnCart_Result["data"];
  status: DataMutationShippingSetBillingAddressesOnCart_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationShippingSetBillingAddressesOnCartConsumerInterface;
const DataMutationShippingSetBillingAddressesOnCartContext =
  React.createContext(defaultValue);

export interface DataMutationShippingSetBillingAddressesOnCart_ControllerInterface {
  mutate: DataMutationShippingSetBillingAddressesOnCart_Result["mutate"];
  mutateAsync: DataMutationShippingSetBillingAddressesOnCart_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationShippingSetBillingAddressesOnCart_ControllerInterface;
const DataMutationShippingSetBillingAddressesOnCart_ControllerContext =
  React.createContext(controllerDefaultValue);

export interface DataMutationShippingSetBillingAddressesOnCart_Request
  extends SetBillingAddressOnCartInput {}
export type DataMutationShippingSetBillingAddressesOnCart_Result =
  ReactQuery.UseMutationResult<
    CheckoutModel_MyBag,
    unknown,
    DataMutationShippingSetBillingAddressesOnCart_Request,
    unknown
  >;
export type GraphqlResponse_ShippingSetBillingAddressesOnCart =
  GraphqlWrappedData<
    "setBillingAddressOnCart",
    {
      cart: CheckoutModel_MyBag;
    }
  >;

export interface DataMutationShippingSetBillingAddressesOnCartProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationShippingSetBillingAddressesOnCartProvider: React.FunctionComponent<
  DataMutationShippingSetBillingAddressesOnCartProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const [genericDataPortValues, genericDataPortController] =
    useGenericDataPortForMyBag();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutation_Shipping_SetBillingAddressesOnCart_QueryKey.shippingSetBillingAddressesOnCart();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    CheckoutModel_MyBag,
    DataMutationShippingSetBillingAddressesOnCart_Request
  > = React.useCallback(
    async (request) => {
      const gqlQuery = `
        mutation {
          setBillingAddressOnCart(
            input: {
              cart_id: "${request.cart_id}"
              billing_address: {
                address: {
                  firstname: "${
                    request.billing_address.address.firstname || ""
                  }"
                  lastname: "${request.billing_address.address.lastname || ""}"
                  company: null
                  street: [${request.billing_address.address.street
                    .map(
                      (this_street) => `
                        "${this_street || ""}"
                      `
                    )
                    .join(", ")}]
                  city: "${request.billing_address.address.city || ""}"
                  region: "${request.billing_address.address.region || ""}"
                  region_id: ${request.billing_address.address.region_id || 0}

                  postcode: "${request.billing_address.address.postcode || ""}"
                  country_code: "${
                    request.billing_address.address.country_code || ""
                  }"
                  telephone: "${
                    request.billing_address.address.telephone || ""
                  }"
                  save_in_address_book: false
                }
                same_as_shipping: false
              }
            }
          ) {
            cart {
              ${CheckoutModel_MyBag_Graphql_Request}
            }
          }
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } =
        await axios.post<GraphqlResponse_ShippingSetBillingAddressesOnCart>(
          "/graphql",
          { query: gqlQuery },
          { headers }
        );

      genericDataPortController.portError(data);

      const { data: gqlResponseData } = data || {};
      const { setBillingAddressOnCart } = gqlResponseData || {};
      const { cart } = setBillingAddressOnCart || {};

      genericDataPortController.portSuccess();

      return cart;
    },
    [initialState, genericDataPortController]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess: genericDataPortController.useMutationOrQueryOnSuccess,
  });

  const ignoreMyCurrentState =
    genericDataPortController.ignoreMyCurrentStateFactory(mutation);
  const value: DataMutationShippingSetBillingAddressesOnCartConsumerInterface =
    React.useMemo(
      () => ({
        data: mutation.data,
        status: mutation.status,
        error: mutation.error,

        dataUpdatedAt: genericDataPortValues.dataUpdatedAt,
        errorUpdatedAt: genericDataPortValues.errorUpdatedAt,
        ignoreMyCurrentState: ignoreMyCurrentState,
      }),
      [
        mutation.data,
        mutation.status,
        mutation.error,

        genericDataPortValues.dataUpdatedAt,
        genericDataPortValues.errorUpdatedAt,
        ignoreMyCurrentState,
      ]
    );
  const controllerValue: DataMutationShippingSetBillingAddressesOnCart_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationShippingSetBillingAddressesOnCartContext.Provider
      value={value}
    >
      <DataMutationShippingSetBillingAddressesOnCart_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationShippingSetBillingAddressesOnCart_ControllerContext.Provider>
    </DataMutationShippingSetBillingAddressesOnCartContext.Provider>
  );
};

export const useDataMutationShippingSetBillingAddressesOnCart = () => {
  const context = React.useContext(
    DataMutationShippingSetBillingAddressesOnCartContext
  );
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationShippingSetBillingAddressesOnCart must be used within a DataMutationShippingSetBillingAddressesOnCartProvider"
    );
  }
  return context;
};

export const useDataMutationShippingSetBillingAddressesOnCart_asController =
  () => {
    const context = React.useContext(
      DataMutationShippingSetBillingAddressesOnCart_ControllerContext
    );
    if (context === controllerDefaultValue) {
      throw new Error(
        "useDataMutationShippingSetBillingAddressesOnCart_asController must be used within a DataMutationShippingSetBillingAddressesOnCartProvider"
      );
    }
    return context;
  };
